// FAQ/홈 설정이 공유하는 draft→검증→배포→롤백 데이터 레이어. inAppAnnouncements와 동일하게
// Cloudflare Function 없이 client가 Firebase SDK로 직접 Firestore에 쓰고, firestore.rules가
// 유일한 서버측 게이트다(apps/admin/src/lib/data.js의 saveAnnouncement/addAudit 패턴을 그대로 따름).
import { collection, doc, getDoc, getDocs, limit, orderBy, query, serverTimestamp, writeBatch } from 'firebase/firestore'
import { addAudit, formatDateTime, requireDb, toDate } from './data'
import { validateCmsDraft } from '../../../../packages/validation/cms'
import { emptyCmsData } from '../../../../packages/types/cms'

function itemCountOf(data) {
  if (Array.isArray(data?.items)) return data.items.length
  return undefined
}

function productionRef(db, contentType) {
  return doc(db, 'cmsContent', contentType, 'production', 'current')
}
function draftRef(db, contentType) {
  return doc(db, 'cmsContent', contentType, 'draft', 'current')
}
function versionRef(db, contentType, version) {
  return doc(db, 'cmsContent', contentType, 'versions', String(version))
}

export async function fetchCmsContent(contentType) {
  const db = requireDb()
  const [productionSnap, draftSnap] = await Promise.all([
    getDoc(productionRef(db, contentType)),
    getDoc(draftRef(db, contentType)),
  ])
  const production = productionSnap.exists() ? productionSnap.data() : null
  const draft = draftSnap.exists() ? draftSnap.data() : null
  return { production, draft }
}

export async function fetchCmsVersions(contentType, max = 50) {
  const db = requireDb()
  const snapshot = await getDocs(query(collection(db, 'cmsContent', contentType, 'versions'), orderBy('version', 'desc'), limit(max)))
  return snapshot.docs.map(item => item.data())
}

export async function saveCmsDraft(contentType, data, { uid, baseVersion = 0 }) {
  const db = requireDb()
  const schemaVersion = emptyCmsData(contentType).schemaVersion
  const payload = { contentType, schemaVersion, data, baseVersion, savedAt: serverTimestamp(), savedBy: uid }
  const batch = writeBatch(db)
  batch.set(draftRef(db, contentType), payload)
  addAudit(batch, { action: 'draft-save', entityType: `cms-${contentType === 'homeConfig' ? 'home' : contentType}`, entityId: contentType, actorUid: uid, before: null, after: { contentType, itemCount: itemCountOf(data) } })
  await batch.commit()
}

export async function discardCmsDraft(contentType, uid) {
  const db = requireDb()
  const batch = writeBatch(db)
  batch.delete(draftRef(db, contentType))
  addAudit(batch, { action: 'delete', entityType: `cms-${contentType === 'homeConfig' ? 'home' : contentType}`, entityId: contentType, actorUid: uid, before: null, after: null })
  await batch.commit()
}

// 검증 실패 시 배포하지 않고 그대로 에러를 던진다 — 호출부(에디터 화면)가 이미 UI에서 같은 검증
// 결과를 보여줬어야 하므로, 여기서 막히는 것은 우회 시도이거나 방어적 이중 확인이다.
export async function publishCmsContent(contentType, data, { uid, note = null }) {
  const validation = validateCmsDraft(contentType, data)
  if (!validation.valid) {
    const error = new Error('치명적 오류가 있어 배포할 수 없습니다.')
    error.validation = validation
    throw error
  }
  const db = requireDb()
  const currentSnap = await getDoc(productionRef(db, contentType))
  const nextVersion = (currentSnap.exists() ? currentSnap.data().version : 0) + 1
  const schemaVersion = emptyCmsData(contentType).schemaVersion
  const payload = { contentType, schemaVersion, version: nextVersion, data, publishedAt: serverTimestamp(), publishedBy: uid, note }
  const batch = writeBatch(db)
  batch.set(productionRef(db, contentType), payload)
  batch.set(versionRef(db, contentType, nextVersion), payload)
  batch.delete(draftRef(db, contentType))
  addAudit(batch, { action: note ? 'rollback' : 'publish', entityType: `cms-${contentType === 'homeConfig' ? 'home' : contentType}`, entityId: contentType, actorUid: uid, before: currentSnap.exists() ? { contentType, version: currentSnap.data().version } : null, after: { contentType, version: nextVersion, itemCount: itemCountOf(data) } })
  await batch.commit()
  return nextVersion
}

export async function rollbackCmsContent(contentType, targetVersion, { uid }) {
  const db = requireDb()
  const snapshot = await getDoc(versionRef(db, contentType, targetVersion))
  if (!snapshot.exists()) throw new Error(`버전 ${targetVersion}을(를) 찾을 수 없습니다.`)
  return publishCmsContent(contentType, snapshot.data().data, { uid, note: `rollback-to-v${targetVersion}` })
}

export { toDate, formatDateTime }
